import { yupResolver } from '@hookform/resolvers/yup';
import { Button } from '@iwoca/orion';
import { FormProvider, useForm } from 'react-hook-form';

import { businessTypeValidator } from './businessTypeValidator';
import { SoleTraderOption } from './SoleTraderOption/SoleTraderOption';
import { InlineButton } from '../../../components/InlineButton/InlineButton';
import { WhiteLabelOverride } from '../../../components/WhiteLabelOverride/WhiteLabelOverride';
import { useSoleTraderPilot } from '../../../hooks/useSoletraderPilot';
import { HookFormRadioButton } from '../../components/RadioButton/RadioButton';
import { StepCard } from '../../components/StepCard/StepCard';
import { BUSINESS_DETAILS_STEP_ID, COMPANY_TYPE_STEP_ID } from '../Steps';
import { useNavigateToStep } from '../useNavigateToStep';
import { useStepData } from '../useStepData';
import { useStepStatus } from '../useStepStatus';

export const BUSINESS_TYPE_OPTIONS = [
  {
    label: 'Limited Company',
    value: 'limited_liability_company',
  },
  {
    label: 'Limited Liability Partnership',
    value: 'limited_liability_partnership',
  },
  { label: 'Sole Trader', value: 'sole_trader' },
] as const;

export const BusinessType = () => {
  const { stepData, updateStepData, isUpdatingStepData } =
    useStepData(COMPANY_TYPE_STEP_ID);
  const stepStatus = useStepStatus(COMPANY_TYPE_STEP_ID);
  const isActive = stepStatus === 'active';
  const isCompleted = stepStatus === 'complete';

  const goToCurrentStep = useNavigateToStep(COMPANY_TYPE_STEP_ID);
  const goToNextStep = useNavigateToStep(BUSINESS_DETAILS_STEP_ID);

  const methods = useForm({
    defaultValues: stepData,
    resolver: yupResolver(businessTypeValidator),
  });

  const companyType = methods.watch('companyType');
  const isSoleTrader = companyType === 'sole_trader';
  const isSoleTraderPilot = useSoleTraderPilot();
  const showSoleTraderPayNowButton = isSoleTrader && !isSoleTraderPilot;

  const handleSubmit = async (values: typeof stepData) => {
    await updateStepData(values);
    goToNextStep();
  };

  const onSubmit = methods.handleSubmit(handleSubmit);

  return (
    <StepCard title="1. Business type" isCompleted={isCompleted}>
      <FormProvider {...methods}>
        {isActive ? (
          <form onSubmit={onSubmit}>
            <HookFormRadioButton
              labelText="What type of business are you?"
              name="companyType"
              options={BUSINESS_TYPE_OPTIONS}
              className="min-w-[500px] xs:min-w-0"
            />
            {showSoleTraderPayNowButton ? (
              <SoleTraderOption />
            ) : (
              <WhiteLabelOverride
                componentType="button"
                disabled={isUpdatingStepData}
              >
                <Button
                  disabled={isUpdatingStepData}
                  loading={isUpdatingStepData}
                  className="mt-m sm:w-full"
                >
                  Continue to business info
                </Button>
              </WhiteLabelOverride>
            )}
          </form>
        ) : (
          <div>
            <div className="mb-l font-reg">
              {
                BUSINESS_TYPE_OPTIONS.find(({ value }) => value === companyType)
                  ?.label
              }
            </div>
            <InlineButton onClick={goToCurrentStep}>Edit</InlineButton>
          </div>
        )}
      </FormProvider>
    </StepCard>
  );
};
